/**
 * THE READ-THROUGH — the flow, in sentences, in the order a caller hears them.
 *
 * WHY THIS IS THE MOST IMPORTANT PANEL ON THE SCREEN. A canvas is a picture, and a picture of
 * a call flow is only useful to somebody who can already read call flows. The person buying
 * this is an ISP owner who wants to know what their phone says at 2 AM. So the canvas has a
 * plain-English column beside it, and the test of a flow is whether the owner can read that
 * column out loud and recognise their own phone line.
 *
 * It is also the accessibility answer. A drag-and-drop graph is not operable by everyone, and
 * "add a screen-reader label to each node" does not fix that — a screen reader reading twelve
 * disconnected cards still cannot tell you what happens after the second one. This panel is
 * an ordered list of sentences, which is the same information in a form a screen reader is
 * good at. It is not a fallback; it is on screen for everybody, all the time.
 *
 * WHY IT USES THE SERVER'S COMPILER. `src/api/flow-compile.ts` is pure, dependency-free (its
 * one import is a type, and types are erased), and it is the exact code the save route runs.
 * Importing it means the errors on the canvas ARE the errors the server will produce, before
 * anybody presses Save — rather than a second, drifting, client-side approximation that is
 * right until the day it matters. The server stays authoritative: it recompiles and it can
 * still refuse for reasons the browser cannot know (a tool this tenant's policy denies).
 */
import { compileFlow } from '../../../../src/api/flow-compile.ts';
import type { CompileIssue, CompileResult, FlowGraph, FlowNodeType } from '../../../../src/api/flow-compile.ts';
import type { PlaybookStep } from '../../../../src/api/contract.ts';

import { toWire } from './graph.ts';
import type { NdGraph } from './graph.ts';
import { isDoTool, toolLabel } from './model.ts';

export type { CompileIssue, CompileResult };

export function compilePreview(graph: NdGraph): CompileResult {
  const wire = toWire(graph);
  const input: FlowGraph = {
    nodes: wire.nodes.map((n) => ({
      id: n.id,
      type: n.type as FlowNodeType,
      position: n.position,
      data: n.data,
    })),
    edges: wire.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? null,
    })),
  };
  return compileFlow(input);
}

/** Issues keyed by node, so a card can render its own problem instead of a list at the bottom. */
export function issuesByNode(issues: CompileIssue[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const issue of issues) {
    if (!issue.nodeId) continue;
    const list = map.get(issue.nodeId) ?? [];
    list.push(issue.message);
    map.set(issue.nodeId, list);
  }
  return map;
}

/** The issues that belong to the flow rather than to any one node. */
export function flowLevelIssues(issues: CompileIssue[]): string[] {
  return issues.filter((i) => !i.nodeId).map((i) => i.message);
}

export type Line = {
  /** Indent level — a branch case indents what follows it, the way a person would read it. */
  depth: number;
  /** 'speech' is quoted verbatim; the rest is narration. */
  kind: 'speech' | 'narration' | 'case' | 'rule';
  text: string;
  /** Rendered as a quotation when present. */
  quote?: string;
};

/**
 * Steps → sentences. The runtime step list is a flat array with `branch` markers in it, so
 * the depth here is derived the way the compiler emitted it: a branch marker opens a case and
 * everything until the next branch marker at the same level belongs to it. That is a
 * simplification of an arbitrarily nested flow, and it is the honest one — the compiler
 * itself flattens, so anything cleverer here would describe a flow the phone does not run.
 */
export function readThrough(steps: PlaybookStep[]): Line[] {
  const lines: Line[] = [];
  let depth = 0;

  for (const step of steps) {
    switch (step.type) {
      case 'say':
        lines.push({ depth, kind: 'speech', text: 'The assistant says', quote: step.text ?? '' });
        break;

      case 'ask':
        lines.push({
          depth,
          kind: 'speech',
          text: step.note ? `It asks, and remembers the answer as “${step.note}”` : 'It asks',
          quote: step.text ?? '',
        });
        break;

      case 'branch': {
        const note = step.note ?? '';
        const eq = note.indexOf('=');
        const on = eq >= 0 ? note.slice(0, eq) : note;
        const value = eq >= 0 ? note.slice(eq + 1) : '';
        depth = 1;
        // A branch from an older template carries no recorded answer; it read `If the answer
        // is "" —`. Say what is true instead.
        lines.push({
          depth: 0,
          kind: 'case',
          text: value ? `If ${on || 'the answer'} is “${value}” —` : `Depending on ${on || 'the answer'} —`,
        });
        break;
      }

      case 'tool': {
        const tool = step.tool ?? '';
        const note = step.note ?? '';
        if (tool === 'arm_situation') {
          lines.push({
            depth,
            kind: 'narration',
            text: `It switches the line to ${note || 'a mode'}.`,
          });
          lines.push({
            depth,
            kind: 'rule',
            text: 'Every caller after this one hears that mode, until somebody releases it.',
          });
        } else if (note.startsWith('gated')) {
          lines.push({
            depth,
            kind: 'narration',
            text: `It proposes: ${toolLabel(tool).toLowerCase()}.`,
          });
          lines.push({
            depth,
            kind: 'rule',
            text: 'Nothing happens until a person approves that proposal.',
          });
        } else if (isDoTool(tool)) {
          // An action that runs without a proposal (an older template's ticket step) is not a
          // lookup; "It looks up: open a ticket." read wrong.
          lines.push({ depth, kind: 'narration', text: `It does: ${toolLabel(tool).toLowerCase()}.` });
        } else {
          lines.push({ depth, kind: 'narration', text: `It looks up: ${toolLabel(tool).toLowerCase()}.` });
        }
        break;
      }

      case 'take_message': {
        const note = step.note ?? '';
        if (note.includes('page on-call')) {
          lines.push({ depth, kind: 'narration', text: 'The caller leaves a voicemail, and then on-call is paged.' });
          lines.push({ depth, kind: 'rule', text: 'The caller is never connected to a technician.' });
        } else {
          lines.push({ depth, kind: 'narration', text: `The caller leaves a message, filed as ${note || 'customer'}.` });
        }
        break;
      }

      case 'sms_link':
        lines.push({ depth, kind: 'narration', text: 'It offers to send a link by text.' });
        break;

      case 'portal_handoff':
        lines.push({ depth, kind: 'narration', text: 'It hands the caller to the payment portal.' });
        break;

      case 'schedule_callback':
        lines.push({ depth, kind: 'narration', text: 'It schedules a call back.' });
        break;

      case 'end':
        lines.push({ depth, kind: 'narration', text: 'The call ends.' });
        break;

      default:
        lines.push({ depth, kind: 'narration', text: 'A step the console does not recognise.' });
    }
  }

  return lines;
}

/**
 * A one-line summary for the header: "9 steps · 2 things it can say · ends the call".
 * Deliberately counts what an owner counts, not what a graph theorist counts.
 */
export function summarise(steps: PlaybookStep[]): string {
  if (steps.length === 0) return 'This flow does not do anything yet.';
  const says = steps.filter((s) => s.type === 'say' || s.type === 'ask').length;
  const gated = steps.filter((s) => s.type === 'tool' && (s.note ?? '').startsWith('gated')).length;
  const parts = [`${steps.length} step${steps.length === 1 ? '' : 's'}`];
  if (says) parts.push(`${says} thing${says === 1 ? '' : 's'} it says`);
  if (gated) parts.push(`${gated} awaiting approval`);
  const endsIt = steps[steps.length - 1]?.type === 'end';
  parts.push(endsIt ? 'ends the call' : 'no ending step');
  return parts.join(' · ');
}
